import React, { useState } from 'react';
import classNames from 'classnames';
import './LeadPopupDesignForm.pcss';
import TextInput from '../../../../../../common/components/TextInput';
import { ColorPicker } from '../../../../../../common/components/ColorPicker/ColorPicker';
import { LeadPopupPreview } from './LeadPopupPreview/LeadPopupPreview';
import { PromoterModel, OptinSettings } from '@mm/state/model/PromoterModel';
import ImageUploader from '../../../../../../common/components/ImageUploader/ImageUploader';
import Checkbox from '../../../../../../common/components/Checkbox';
import { PreviewBox } from '../common/PreviewBox/PreviewBox';

type Props = {
  className?: string;
  promoter: PromoterModel;
  onUpdate: (promoter: PromoterModel) => void;
};

const FIELDS = {
  description: 'popupDescription',
  title: 'popupTitle',
  sms: 'popupCtaSmsButtonText',
};

const COLORS = ['#9462F5', '#92DDA5', '#4F5EF0', '#E7507C'];

const ColorOptions = ({
  options,
  selectedColor,
  onSelect,
}: {
  options: string[];
  selectedColor?: string;
  onSelect: (color: string) => void;
}) => {
  const isCustom = selectedColor && !options.includes(selectedColor);
  const [isPickerActive, setIsPickerActive] = useState(false);
  const onAddCustomColorClick = () => setIsPickerActive(true);
  const onCustomColorSelect = (c: string) => {
    setIsPickerActive(false);
    onSelect(c);
  };
  return (
    <div className="color-options">
      {options.map(color => (
        <div
          onClick={() => onSelect(color)}
          className={classNames('color-options__color', {
            'color-options__color--active':
              color.toUpperCase() === selectedColor?.toUpperCase(),
          })}
          style={{ backgroundColor: color }}
        />
      ))}

      {isCustom && (
        <div
          className="color-options__color color-options__color--active"
          style={{ backgroundColor: selectedColor }}
        />
      )}

      <span
        className="color-options__custom-btn"
        onClick={onAddCustomColorClick}
      >
        + Add custom color
      </span>

      {isPickerActive && (
        <ColorPicker color="black" onColorChange={onCustomColorSelect} />
      )}
    </div>
  );
};

export const LeadPopupDesignForm = ({
  className,
  promoter,
  onUpdate,
}: Props) => {
  const { optinSettings } = promoter;
  const {
    popupColor,
    popupButtonColor,
    popupTitle,
    popupDescription,
    popupCtaSmsButtonText,
    popupImageUrl,
    popupIncludesImage,
  } = optinSettings || {};

  const [isImageIncluded, setIsImageIncluded] = useState(popupIncludesImage);

  const [textFields, setTextFields] = useState({
    popupTitle,
    popupDescription,
    popupCtaSmsButtonText,
  });

  const updateSettings = (up: OptinSettings) => {
    const updatedSettings = { ...(optinSettings || {}), ...up };
    onUpdate({ ...promoter, optinSettings: updatedSettings });
  };

  const onPopupColorChange = (color: string) =>
    updateSettings({ popupColor: color });
  const onButtonColorChange = (color: string) =>
    updateSettings({ popupButtonColor: color });

  const onFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setTextFields({ ...textFields, [id]: value });
  };

  const onFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    updateSettings({ [id]: value });
  };

  const onImageUpload = (_, popupImageUrl: string) =>
    updateSettings({ popupImageUrl });

  const onImageRemove = () => updateSettings({ popupImageUrl: null });

  const onImageIncludedToggle = () => {
    setIsImageIncluded(!isImageIncluded);
    updateSettings({ popupIncludesImage: !isImageIncluded });
  };

  return (
    <div className={classNames(className, 'lead-popup-design-form__container')}>
      <div className="lead-popup-design-form__config">
        <div className="lead-popup-design-form__popup-color">
          <span className="lead-popup-design-form__config-item-name">
            Popup color
          </span>
          <ColorOptions
            options={COLORS}
            selectedColor={popupColor}
            onSelect={onPopupColorChange}
          />
        </div>
        <div className="lead-popup-design-form__button-color">
          <span className="lead-popup-design-form__config-item-name">
            Button color
          </span>
          <ColorOptions
            options={COLORS}
            selectedColor={popupButtonColor}
            onSelect={onButtonColorChange}
          />
        </div>

        <div className="lead-popup-design-form__input-wrapper">
          <span className="lead-popup-design-form__config-item-name">
            Title
          </span>
          <TextInput
            className="lead-popup-design-form__input"
            id={FIELDS.title}
            maxLength={50}
            value={textFields.popupTitle}
            onChange={onFieldChange}
            onBlur={onFieldBlur}
          />
        </div>

        <div className="lead-popup-design-form__input-wrapper">
          <span className="lead-popup-design-form__config-item-name">
            Description
          </span>
          <TextInput
            className="lead-popup-design-form__input"
            id={FIELDS.description}
            maxLength={1000}
            value={textFields.popupDescription}
            onChange={onFieldChange}
            onBlur={onFieldBlur}
          />
        </div>

        <div className="lead-popup-design-form__image">
          <Checkbox
            className="lead-popup-design-form__checkbox"
            isChecked={!!isImageIncluded}
            onToggle={onImageIncludedToggle}
            label="Include image"
          />
          <ImageUploader
            disabled={!isImageIncluded}
            className="lead-popup-design-form__checkbox__image-uploader"
            file={popupImageUrl || null}
            onUpload={onImageUpload}
            disableCrop={true}
            withRemoveBtn={true}
            onRemove={onImageRemove}
          />
        </div>

        <div className="lead-popup-design-form__cta-text">
          Call to action text:
        </div>
        <div className="lead-popup-design-form__cta-text-sms">
          <label>
            SMS
            <TextInput
              className="lead-popup-design-form__input"
              id={FIELDS.sms}
              maxLength={20}
              value={textFields.popupCtaSmsButtonText}
              onChange={onFieldChange}
              onBlur={onFieldBlur}
            />
          </label>
        </div>
      </div>

      <div className="lead-popup-design-form__preview">
        <PreviewBox>
          {({ isMobile }) => (
            <LeadPopupPreview
              isMobile={isMobile}
              className="lead-popup-confirmation-form__popup"
              title={textFields.popupTitle}
              description={textFields.popupDescription}
              withImage={isImageIncluded}
              imgSrc={popupImageUrl}
              ctaText={textFields.popupCtaSmsButtonText}
              popupColor={popupColor}
              buttonColor={popupButtonColor}
            />
          )}
        </PreviewBox>
      </div>
    </div>
  );
};
